"use client";

import React, { useEffect, useState } from "react";
import { PagePayoutSchema } from "@/sanity/pages/PagePayoutTemplate";

import { cn } from "@/lib/utils";
import Image from "next/image";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { CTAButton } from "@/shadcn/ui/button";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/shadcn/ui/carousel";

import laurels from "@/public/payout/laurels.svg";
import ipad from "@/public/global/ipad.png";
import { PageAssetsSchema } from "@/sanity/pages/PageAssetsTemplate";
import { motion } from "framer-motion";
import bulletIcon from "@/public/assets/bulletIcon.svg";

import PricingCardsGrid, {
  AnyPricingCard,
} from "@/components/pageComponents/global/PricingCardsGrid";
import {
  PricingHeroBlock,
  PricingTiersBlock,
  PricingComparisonBlock,
  PricingPlatformsBlock,
} from "@/components/pageComponents/pricingSectionV2";
import NewPricingSection from "@/components/pageComponents/newPricing/NewPricingSection";

export default function PlanSection({
  data,
  className,
}: {
  data: PageAssetsSchema;
  className?: string;
}) {
  // V3: the new pricing section brings its own container/padding — render it
  // full-bleed, locked to this page's asset.
  if (data.planSectionV3) {
    return (
      <section className={cn("relative w-full", className)}>
        <NewPricingSection
          sectionV3={data.planSectionV3}
          section={data.planSectionV2}
          language={data.settings?.language}
          fixedAsset={data.pageStyle === "futures" ? "futures" : "cfd_crypto"}
        />
      </section>
    );
  }

  return (
    <section className={cn("relative", className)}>
      <div className="container max-w-none w-[min(170rem,100%)] flex flex-col items-start lg:items-center py-[8rem] lg:py-[16rem]">
        {!data.planSectionV2 && (
          <motion.div
            className="richtext w-[min(75.625rem,100%)] prose-headings:mb-[1.5rem] prose-p:lg:max-w-[58.625rem]
           "
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            {data?.planText && (
              <PortableText
                value={data?.planText}
                components={richTextComponent}
              />
            )}
          </motion.div>
        )}

        {data.planSectionV2 ? (
          (() => {
            const restrictedAssetKey =
              data.pageStyle === 'futures' ? 'futures' : 'cfd_crypto';
            const tiersFilter = { assetKey: restrictedAssetKey };
            return (
              <div className="flex w-full flex-col gap-[6rem]">
                <PricingHeroBlock
                  data={data.planSectionV2}
                  language={data.settings?.language}
                />
                <PricingTiersBlock
                  data={data.planSectionV2}
                  language={data.settings?.language}
                  filter={tiersFilter}
                  discount={data?.pricingV2Discount}
                />
                {data.planSectionV2.comparison &&
                data.planSectionV2.specsCatalog?.length ? (
                  <PricingComparisonBlock
                    data={data.planSectionV2}
                    language={data.settings?.language}
                    filter={{ assetKey: restrictedAssetKey }}
                    discount={data?.pricingV2Discount}
                  />
                ) : null}
                {data.planSectionV2.platformsFooter && (
                  <PricingPlatformsBlock
                    data={data.planSectionV2.platformsFooter}
                    language={data.settings?.language}
                    filter={{ assetKey: restrictedAssetKey }}
                  />
                )}
              </div>
            );
          })()
        ) : (
          <PricingCardsGrid
            cards={(data.planCardRefs ?? []) as AnyPricingCard[]}
            defaultAssetTab={
              data.pageStyle === "futures" ? "secondary" : "primary"
            }
            challengeAccountsLabel={data.planChallengeAccountsLabel}
            instantFundingLabel={data.planInstantFundingLabel}
            challengeLabel={data.planChallengeLabel}
            fundedLabel={data.planFundedLabel}
            language={data.settings?.language}
            discountSettings={data?.discountSettings}
          />
        )}
      </div>
    </section>
  );
}
